// The macro Body's keyword reference, beside the box you type the body into.
//
// RENDERED AS A SIBLING OF THE FORM'S <fieldset>, NEVER INSIDE IT. DescriptorForm's
// fieldset carries `disabled={!mayWrite}`, and a disabled fieldset disables every
// descendant form control except those in its first <legend> — `<button>` included.
// Inside it, the person this panel is most useful to (someone below lead, reading a
// macro rather than editing it) would get Copy buttons that silently do nothing.
//
// The list comes from ../create/macro-placeholders.js, the same module
// `fillMacroTemplate` resolves from, so a keyword cannot be advertised here that
// nothing substitutes there.

import { useRef, useState } from 'react';

import { MACRO_PLACEHOLDERS } from '../create/macro-placeholders.js';

/**
 * One keyword, its description, and a Copy button.
 *
 * The clipboard is FEATURE-DETECTED AT CLICK TIME AND THE PROMISE IS CAUGHT. Both
 * environments are secure contexts (localhost in dev, HTTPS in production), so
 * `navigator.clipboard` will be defined — what actually varies is whether the write
 * is permitted, and a `.then()` that reports "Copied" with no `.catch()` is a button
 * that claims success and did nothing. On any failure the keyword text is SELECTED
 * instead, so Ctrl+C finishes the job, and the message says so.
 */
function KeywordRow({ placeholder }) {
  const [state, setState] = useState('idle');
  const textRef = useRef(null);
  const timer = useRef(null);

  const token = `{{${placeholder.key}}}`;

  function announce(next) {
    setState(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), 1500);
  }

  function selectToken() {
    const node = textRef.current;
    if (!node || typeof window === 'undefined') return;
    const range = document.createRange();
    range.selectNodeContents(node);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  async function copy() {
    if (!navigator.clipboard?.writeText) {
      selectToken();
      announce('manual');
      return;
    }
    try {
      await navigator.clipboard.writeText(token);
      announce('copied');
    } catch {
      selectToken();
      announce('manual');
    }
  }

  return (
    <li className="border-t border-line pt-2 first:border-t-0 first:pt-0">
      <div className="flex items-start justify-between gap-2">
        <code ref={textRef} className="font-mono text-xs break-all text-ink">{token}</code>
        <button
          type="button"
          className="btn-ghost shrink-0 px-2 py-0.5 text-xs"
          onClick={() => { void copy(); }}
        >
          {state === 'copied' ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p className="mt-0.5 text-xs text-ink-muted">{placeholder.description}</p>
      {state === 'manual' && (
        <p className="mt-0.5 text-xs text-ink-secondary">Selected. Press Ctrl+C to copy.</p>
      )}
      {/* The only other feedback is a word changing on a small button, which a
          screen reader never announces. Same treatment ThemeToggle established. */}
      <span aria-live="polite" className="sr-only">
        {state === 'copied' ? `${token} copied` : ''}
        {state === 'manual' ? `${token} selected, press Control C to copy` : ''}
      </span>
    </li>
  );
}

export default function KeywordPanel() {
  return (
    <aside className="h-fit rounded-lg border border-line bg-surface p-4">
      <h2 className="text-sm font-semibold text-ink">Keywords</h2>
      <p className="mt-1 text-xs text-ink-muted">
        Paste one into the Body. It is replaced when an agent applies the macro.
      </p>

      <ul className="mt-3 grid gap-2">
        {MACRO_PLACEHOLDERS.map((placeholder) => (
          <KeywordRow key={placeholder.key} placeholder={placeholder} />
        ))}
      </ul>

      {/* Three real behaviours a macro author is otherwise surprised by. The third
          reads as a bug if nobody says it out loud. */}
      <div className="mt-4 border-t border-line pt-3 text-xs text-ink-muted">
        <p>Case does not matter, and inner spaces are fine: {'{{ Customer_Name }}'} works.</p>
        <p className="mt-1">
          A keyword nothing recognises is left in the message exactly as typed, rather than
          blanked, because an unfinished macro should look unfinished.
        </p>
        <p className="mt-1">
          So is a keyword with nothing behind it yet: apply a macro before picking a caller and
          {' '}{'{{customer_name}}'} stays in the composer.
        </p>
      </div>
    </aside>
  );
}
